import { motion } from "framer-motion";
import { Target, Heart, GraduationCap, Sparkles, Clock, Users } from "lucide-react";

const reasons = [
  {
    icon: Target,
    title: "Estrategia personalizada",
    desc: "Cada negocio es único. Diseño un plan a medida según tu etapa, presupuesto y objetivos reales — nada de fórmulas genéricas.",
  },
  {
    icon: Heart,
    title: "Pasión por lo que hago",
    desc: "Lo que más me motiva es ver crecer tu proyecto. Me involucro como si fuera mío y celebro tus resultados como propios.",
  },
  {
    icon: GraduationCap,
    title: "Formación actualizada",
    desc: "Estudio Marketing Digital y E-commerce, así que aplico siempre las mejores prácticas y herramientas más actuales del mercado.",
  },
  {
    icon: Sparkles,
    title: "Mirada integral",
    desc: "Desde la auditoría hasta la tienda online: te acompaño en todo el camino digital sin necesidad de coordinar varios proveedores.",
  },
  {
    icon: Clock,
    title: "Comunicación clara",
    desc: "Respondo rápido, explico sin tecnicismos y te mantengo informada de cada avance. Tu tiempo es tan valioso como el mío.",
  },
  {
    icon: Users,
    title: "Para emprendedoras reales",
    desc: "Pensado para personas que quieren independizarse y despegar. Honestidad, cercanía y resultados, sin humo.",
  },
];

export default function WhyChooseMe() {
  return (
    <section
      id="por-que-elegirme"
      data-testid="why-section"
      className="relative bg-[#0A192F] text-white py-24 lg:py-32 grain-bg overflow-hidden"
    >
      {/* Decorative big letters */}
      <div
        aria-hidden
        className="absolute -top-10 -right-6 lg:-right-10 font-display italic text-[180px] lg:text-[280px] leading-none text-white/[0.04] select-none pointer-events-none"
      >
        why?
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-5">
              — 03 / Por qué elegirme
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              No vendo paquetes.
              <br />
              <span className="italic">Construyo</span> caminos.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base sm:text-lg text-white/75 leading-relaxed">
              Si llegaste hasta acá, probablemente buscás algo distinto a una
              agencia tradicional. Esto es lo que vas a encontrar trabajando
              conmigo.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15">
          {reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.title}
                data-testid={`why-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.07,
                  ease: [0.2, 0.8, 0.2, 1],
                }}
                className="bg-[#0A192F] p-8 lg:p-10 hover:bg-[#112240] transition-colors duration-300"
              >
                <Icon size={24} strokeWidth={1.5} className="text-white" />
                <h3 className="mt-6 font-display text-2xl text-white">
                  {r.title}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed">
                  {r.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Marquee */}
        <div className="mt-20 overflow-hidden border-y border-white/15 py-6">
          <div className="flex gap-12 animate-marquee whitespace-nowrap font-display italic text-2xl lg:text-3xl text-white/80">
            {Array.from({ length: 2 }).map((_, idx) => (
              <div key={idx} className="flex items-center gap-12">
                <span>Estrategia</span>
                <span className="text-white/30">✦</span>
                <span>Conversión</span>
                <span className="text-white/30">✦</span>
                <span>E-commerce</span>
                <span className="text-white/30">✦</span>
                <span>Meta Ads</span>
                <span className="text-white/30">✦</span>
                <span>Branding</span>
                <span className="text-white/30">✦</span>
                <span>Resultados</span>
                <span className="text-white/30">✦</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
